import { Currency } from "@redotech/money/currencies";
import {
  addWeight,
  convertWeight,
  Weight,
  WeightSchema,
  WeightUnit,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum HSCodeSource {
  CLASSIFICATION = "CLASSIFICATION",
  MARKETPLACE = "MARKETPLACE",
  MANUAL = "MANUAL",
  CHECKOUT_QUOTE = "CHECKOUT_QUOTE", // Just zonos for now, but could come from any source
}

const CustomsDeclarationSchema_ = z.object({
  description: z.string(),
  quantity: z.number(),
  itemValue: z.number(),
  currency: createEnum(Currency, { enumName: "Currency" }).nullish(),
  itemWeight: WeightSchema,
  harmonizationCode: z.string().nullish(),
  hsCodeSource: createEnum(HSCodeSource, {
    enumName: "HSCodeSource",
  }).nullish(),
  countryOfOrigin: createEnum(RedoShopifyCountryCode, {
    enumName: "RedoShopifyCountryCode",
  }),
  manufacturer: z.string().nullish(),
  productIdentifier: z.string().nullish(),
});
export const CustomsDeclarationSchema: z.ZodType<CustomsDeclaration> =
  CustomsDeclarationSchema_;
export interface CustomsDeclaration
  extends z.infer<typeof CustomsDeclarationSchema_> {}
export interface ICustomsDeclaration
  extends z.infer<typeof CustomsDeclarationSchema_> {}

export function customsDeclarationsAreEqual({
  a,
  b,
}: {
  a?: CustomsDeclaration | null;
  b?: CustomsDeclaration | null;
}) {
  if (!a || !b) {
    return false;
  }

  return (
    a.description === b.description &&
    a.quantity === b.quantity &&
    a.itemValue === b.itemValue &&
    a.manufacturer == b.manufacturer &&
    a.productIdentifier == b.productIdentifier &&
    convertWeight(a.itemWeight, WeightUnit.OUNCE).value ===
      convertWeight(b.itemWeight, WeightUnit.OUNCE).value &&
    a.harmonizationCode === b.harmonizationCode &&
    a.countryOfOrigin === b.countryOfOrigin
  );
}

export function getCustomsDeclarationsTotalWeight(
  customsDeclarations: CustomsDeclaration[],
): Weight {
  return customsDeclarations.reduce(
    (total: Weight, declaration) => {
      if (declaration.quantity > 0) {
        return addWeight(total, {
          unit: WeightUnit.OUNCE,
          value: declaration.itemWeight.value * declaration.quantity,
        });
      }
      return total;
    },
    { unit: WeightUnit.OUNCE, value: 0 },
  );
}
